import React, { PropsWithChildren } from 'react';
import { useAppState } from '../../overmind';
import { AuthenticationStatus, OrganizationLevelType } from '../../enums';
import { Navigate, useSearchParams } from 'react-router';
import { Mode, PagePath } from '../navigation.enums';
import { PermissionType } from '../../enums/permission-type';
import { extractHighestOrganizationLevel } from '../../library/helpers/permissions/extract-highest-organization-level';
import { IRouteGuardProps } from './guards.models';
import { extractPermission } from '../../library/helpers/permissions/extract-permission';
import { hasRequiredFeatureFlag } from '../../library/helpers/feature-flags';

const REDIRECT_TO = 'redirectTo';

export function RouterGuard(props: PropsWithChildren<IRouteGuardProps>): JSX.Element {
  const { children, authStatusRequired, onboardingStatusRequired, skillAssessmentStatusRequired, modeRequired, permissionTypeRequired, featureFlagsRequired } = props;

  const { isInitialising, authenticationStatus, user, forcedAssessments, activeAssessmentConfig, mode, permissions, companyVariables } = useAppState();

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get(REDIRECT_TO);

  // auth
  if (!!authStatusRequired) {
    if (authStatusRequired === 'Unauthenticated') {
      if (!isInitialising && authenticationStatus === AuthenticationStatus.Authenticated && !!user) {
        return <Navigate to={!!redirectTo ? redirectTo : PagePath.dashboard} />;
      }
    }

    if (authStatusRequired === 'Authenticated') {
      if (authenticationStatus !== AuthenticationStatus.Authenticated) {
        return <Navigate to={`${PagePath.accountBase}${PagePath.accountLogin}?${REDIRECT_TO}=${location.pathname}`} />;
      }
    }
  }

  // feature flags
  if (!!featureFlagsRequired && featureFlagsRequired?.length > 0 && !user?.isSystemAdmin && !hasRequiredFeatureFlag(featureFlagsRequired, companyVariables.featureFlags)) {
    return <Navigate to={PagePath.dashboard} />;
  }

  // onboarding
  if (!!onboardingStatusRequired) {
    if (!user) {
      return <Navigate to={PagePath.error.replace(':type', 'system').replace(':code', 'api')} />;
    }

    if (onboardingStatusRequired === 'Incomplete') {
      if (user.completedOnboarding) {
        return <Navigate to={!!redirectTo ? redirectTo : PagePath.dashboard} />;
      }
    }

    if (onboardingStatusRequired === 'Complete') {
      if (!user.completedOnboarding) {
        return <Navigate to={`${PagePath.onboarding}?${REDIRECT_TO}=${location.pathname}`} />;
      }
    }
  }

  // skill assessment
  if (!!skillAssessmentStatusRequired) {
    if (skillAssessmentStatusRequired === 'Incomplete') {
      if (!forcedAssessments.length && !activeAssessmentConfig) {
        return <Navigate to={!!redirectTo ? redirectTo : PagePath.yourSkills} />;
      }
    }

    if (skillAssessmentStatusRequired === 'Complete') {
      if (!!forcedAssessments.length || !!activeAssessmentConfig) {
        return <Navigate to={`${PagePath.analysisBase}?${REDIRECT_TO}=${location.pathname}`} />;
      }
    }
  }

  // mode
  if (!!modeRequired) {
    if (modeRequired === Mode.UserDashboard) {
      if (mode === Mode.AdminDashboard) {
        return <Navigate to={PagePath.companies} />;
      }
    }

    if (modeRequired === Mode.AdminDashboard) {
      if (!user) {
        return <Navigate to={PagePath.error.replace(':type', 'system').replace(':code', 'api')} />;
      }

      if (!user.isSystemAdmin) {
        return <Navigate to={PagePath.dashboard} />;
      }

      if (mode === Mode.UserDashboard) {
        return <Navigate to={PagePath.dashboard} />;
      }
    }
  }

  // system admin OR permission type
  if (permissionTypeRequired !== undefined) {
    if (!user) {
      return <Navigate to={PagePath.error.replace(':type', 'system').replace(':code', 'api')} />;
    }

    if (!user.isSystemAdmin) {
      // TODO: update as Skills APIs updated for Permissions
      const allowedPermissionTypes = [PermissionType.Assessments, PermissionType.Administration, PermissionType.Analytics, PermissionType.LearningManagement, PermissionType.RoleSkillManagement];
      if (!allowedPermissionTypes.includes(permissionTypeRequired)) {
        return <Navigate to={PagePath.dashboard} />;
      }

      const permission = extractPermission(permissions, permissionTypeRequired);
      if (!permission) {
        return <Navigate to={PagePath.dashboard} />;
      }

      const highestOrganizationLevel = extractHighestOrganizationLevel(permission.organizationLevels);
      if (!highestOrganizationLevel || highestOrganizationLevel.organizationLevel === OrganizationLevelType.Learner) {
        return <Navigate to={PagePath.dashboard} />;
      }
    }

    if (user.isSystemAdmin && mode === Mode.UserDashboard) {
      return <Navigate to={PagePath.dashboard} />;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
}
